import { PERMISSION_ACTIONS, PERMISSIONS } from "@artisancode/api-types";

import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import type { Permission, PermissionModule } from "@artisancode/api-types";

const PERMISSION_SET = new Set<string>(PERMISSIONS);

export interface PermissionModuleItem {
  key: PermissionModule;
  label: string;
  description?: string;
}

export function PermissionTable({
  modules,
  permissions,
  disabled,
  onToggleModule,
  onTogglePermission,
}: {
  modules: readonly PermissionModuleItem[];
  permissions: Set<Permission>;
  disabled: boolean;
  onToggleModule: (moduleKey: string, checked: boolean) => void;
  onTogglePermission: (p: Permission) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border/70 bg-card">
      <table className="w-full min-w-[560px] text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <th className="py-2.5 px-4 text-left">Modul</th>
            {PERMISSION_ACTIONS.map((a) => (
              <th
                key={a.key}
                className="py-2.5 px-3 text-center w-24 whitespace-nowrap"
              >
                {a.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {modules.map((m) => {
            const modulePermissions = PERMISSION_ACTIONS.map(
              (a) => `${m.key}.${a.key}` as Permission,
            ).filter((p) => PERMISSION_SET.has(p));
            const checkedCount = modulePermissions.filter((p) =>
              permissions.has(p),
            ).length;
            const isAllChecked =
              modulePermissions.length > 0 &&
              checkedCount === modulePermissions.length;
            const isSomeChecked = checkedCount > 0 && !isAllChecked;
            const checkboxState = isAllChecked
              ? true
              : isSomeChecked
                ? "indeterminate"
                : false;

            return (
              <tr
                key={m.key}
                className={cn(
                  "transition-colors hover:bg-muted/30",
                  checkedCount > 0 && "bg-primary/[0.02]",
                )}
              >
                <td className="py-3 px-4">
                  <div className="flex items-start gap-3">
                    <div className="pt-0.5">
                      <Checkbox
                        id={`module-${m.key}`}
                        disabled={disabled}
                        checked={checkboxState}
                        onCheckedChange={() =>
                          onToggleModule(m.key, !isAllChecked)
                        }
                      />
                    </div>
                    <label
                      htmlFor={`module-${m.key}`}
                      className="cursor-pointer select-none space-y-0.5"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground text-sm">
                          {m.label}
                        </span>
                        <span
                          className={cn(
                            "rounded-full px-1.5 py-0.2 text-[10px] font-semibold tabular-nums",
                            isAllChecked
                              ? "bg-primary/15 text-primary"
                              : isSomeChecked
                                ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                                : "bg-muted text-muted-foreground",
                          )}
                        >
                          {checkedCount}/{modulePermissions.length}
                        </span>
                      </div>
                      {m.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {m.description}
                        </p>
                      )}
                    </label>
                  </div>
                </td>
                {PERMISSION_ACTIONS.map((a) => {
                  const p = `${m.key}.${a.key}` as Permission;
                  if (!PERMISSION_SET.has(p)) {
                    return (
                      <td
                        key={a.key}
                        className="py-3 px-3 text-center align-middle"
                      >
                        <span className="text-muted-foreground/30 text-xs select-none">
                          —
                        </span>
                      </td>
                    );
                  }

                  const isChecked = permissions.has(p);
                  return (
                    <td
                      key={a.key}
                      className="py-3 px-3 text-center align-middle"
                    >
                      <div className="flex items-center justify-center">
                        <label
                          className={cn(
                            "flex items-center justify-center rounded-md p-1.5 transition-colors cursor-pointer",
                            disabled && "cursor-not-allowed opacity-50",
                            !disabled && "hover:bg-accent",
                          )}
                        >
                          <Checkbox
                            disabled={disabled}
                            checked={isChecked}
                            onCheckedChange={() => onTogglePermission(p)}
                            aria-label={`${m.label} - ${a.label}`}
                          />
                        </label>
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
